/**
 * DeviceVerifyPage - Verify device for untrusted device login
 *
 * Shown when a user logs in from an untrusted device and has MFA enabled.
 * User can verify with TOTP or email OTP and optionally trust the device.
 */

import { useState, useEffect } from 'react'
import { auth, APIError } from '../api/client'
import { useAppContext } from '../context/AppContext'
import { AuthLayout } from '../components/layout/AuthLayout'
import { AppBrandingHeader } from '../components/auth/AppBrandingHeader'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Alert } from '../components/ui/Alert'
import { Shield, Mail, Smartphone, AlertTriangle } from 'lucide-react'

export function DeviceVerifyPage() {
  const { appInfo } = useAppContext()
  const returnTo = sessionStorage.getItem('u_auth_return_to')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [availableMethods, setAvailableMethods] = useState<string[]>([])
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [trustDevice, setTrustDevice] = useState(true)

  // TOTP state
  const [totpCode, setTotpCode] = useState('')

  // Email OTP state
  const [emailSent, setEmailSent] = useState(false)
  const [emailCode, setEmailCode] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)

  useEffect(() => {
    loadMethods()
  }, [])

  async function loadMethods() {
    try {
      setLoading(true)
      setError(null)
      const { methods } = await auth.getDeviceVerificationMethods()
      setAvailableMethods(methods)

      // Auto-select if only one method
      if (methods.length === 1) {
        setSelectedMethod(methods[0])
      }
    } catch (err) {
      if (err instanceof APIError) {
        if (err.status === 401) {
          // Not authenticated - redirect to login
          window.location.href = '/login'
          return
        }
        setError(err.message)
      } else {
        setError('Failed to load verification methods')
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleSendEmailOtp() {
    setSendingEmail(true)
    setError(null)

    try {
      await auth.sendDeviceVerificationEmail()
      setEmailSent(true)
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('Failed to send email')
      }
    } finally {
      setSendingEmail(false)
    }
  }

  async function handleVerify() {
    if (!selectedMethod) return

    const code = selectedMethod === 'totp' ? totpCode : emailCode
    if (!code || code.length !== 6) {
      setError('Please enter a 6-digit code')
      return
    }

    setVerifying(true)
    setError(null)

    try {
      await auth.verifyDevice({
        method: selectedMethod,
        code,
        trust_device: trustDevice,
      })

      // Success - redirect to next step or return_to
      const redirectUrl = returnTo || import.meta.env.VITE_ACCOUNT_URL || '/'
      sessionStorage.removeItem('u_auth_return_to')
      sessionStorage.removeItem('u_auth_device_id')
      window.location.href = redirectUrl
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('Verification failed')
      }
      // Clear code on error
      if (selectedMethod === 'totp') {
        setTotpCode('')
      } else {
        setEmailCode('')
      }
    } finally {
      setVerifying(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <AuthLayout>
        <AppBrandingHeader
          appInfo={appInfo}
          subtitle="Verifying device for"
          showDefaultBranding={false}
        />
        <Card className="p-8">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
          <p className="mt-4 text-center text-[var(--text-secondary)]">
            Loading verification methods...
          </p>
        </Card>
      </AuthLayout>
    )
  }

  // Error state with no methods
  if (error && availableMethods.length === 0) {
    return (
      <AuthLayout>
        <AppBrandingHeader
          appInfo={appInfo}
          subtitle="Verifying device for"
          showDefaultBranding={false}
        />
        <Card className="p-8">
          <div className="mb-4 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <h2 className="text-center text-lg font-semibold text-[var(--text-primary)]">
            Verification Error
          </h2>
          <p className="mt-2 text-center text-sm text-[var(--text-secondary)]">
            {error}
          </p>
          <div className="mt-6">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => (window.location.href = '/login')}
            >
              Return to Login
            </Button>
          </div>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <AppBrandingHeader
        appInfo={appInfo}
        subtitle="Verifying device for"
        showDefaultBranding={false}
      />

      <Card className="overflow-hidden">
        {/* Header */}
        <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <Smartphone className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="font-semibold text-[var(--text-primary)]">
                New Device Detected
              </h2>
              <p className="text-xs text-[var(--text-secondary)]">
                Verify your identity to continue
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <p className="mb-4 text-sm text-[var(--text-secondary)]">
            We detected a sign-in from a new device. Please verify your identity to
            continue.
          </p>

          {/* Error message */}
          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          {/* Method Selection */}
          {!selectedMethod && availableMethods.length > 1 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-[var(--text-secondary)]">
                Choose a verification method:
              </p>

              {availableMethods.includes('totp') && (
                <button
                  onClick={() => setSelectedMethod('totp')}
                  className="flex w-full items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-4 text-left hover:bg-[var(--bg-elevated)]"
                >
                  <Shield className="h-5 w-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">
                      Authenticator App
                    </p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      Use your authenticator app
                    </p>
                  </div>
                </button>
              )}

              {availableMethods.includes('email') && (
                <button
                  onClick={() => setSelectedMethod('email')}
                  className="flex w-full items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-4 text-left hover:bg-[var(--bg-elevated)]"
                >
                  <Mail className="h-5 w-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">
                      Email Code
                    </p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      Send a verification code to your email
                    </p>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* TOTP Verification */}
          {selectedMethod === 'totp' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary-600" />
                <span className="font-medium text-[var(--text-primary)]">
                  Authenticator App
                </span>
              </div>
              <p className="text-sm text-[var(--text-secondary)]">
                Enter the 6-digit code from your authenticator app.
              </p>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter 6-digit code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-lg tracking-widest"
                autoFocus
              />

              {/* Trust device checkbox */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  className="h-4 w-4 rounded border-[var(--border-default)] bg-[var(--bg-elevated)] text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-[var(--text-secondary)]">
                  Trust this device for 30 days
                </span>
              </label>

              <Button
                className="w-full"
                onClick={handleVerify}
                disabled={verifying || totpCode.length !== 6}
              >
                {verifying ? 'Verifying...' : 'Verify'}
              </Button>

              {availableMethods.length > 1 && (
                <button
                  onClick={() => {
                    setSelectedMethod(null)
                    setTotpCode('')
                    setError(null)
                  }}
                  className="w-full text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  Try a different method
                </button>
              )}
            </div>
          )}

          {/* Email OTP Verification */}
          {selectedMethod === 'email' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary-600" />
                <span className="font-medium text-[var(--text-primary)]">
                  Email Verification
                </span>
              </div>

              {!emailSent ? (
                <>
                  <p className="text-sm text-[var(--text-secondary)]">
                    We'll send a verification code to your email address.
                  </p>
                  <Button
                    className="w-full"
                    onClick={handleSendEmailOtp}
                    disabled={sendingEmail}
                  >
                    {sendingEmail ? 'Sending...' : 'Send Code to Email'}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-[var(--text-secondary)]">
                    We sent a code to your email. Enter it below:
                  </p>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-lg tracking-widest"
                    autoFocus
                  />

                  {/* Trust device checkbox */}
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={trustDevice}
                      onChange={(e) => setTrustDevice(e.target.checked)}
                      className="h-4 w-4 rounded border-[var(--border-default)] bg-[var(--bg-elevated)] text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-[var(--text-secondary)]">
                      Trust this device for 30 days
                    </span>
                  </label>

                  <Button
                    className="w-full"
                    onClick={handleVerify}
                    disabled={verifying || emailCode.length !== 6}
                  >
                    {verifying ? 'Verifying...' : 'Verify'}
                  </Button>

                  <button
                    onClick={handleSendEmailOtp}
                    disabled={sendingEmail}
                    className="w-full text-sm text-[var(--text-primary)] hover:opacity-80 transition-opacity"
                  >
                    {sendingEmail ? 'Sending...' : 'Resend code'}
                  </button>
                </>
              )}

              {availableMethods.length > 1 && (
                <button
                  onClick={() => {
                    setSelectedMethod(null)
                    setEmailSent(false)
                    setEmailCode('')
                    setError(null)
                  }}
                  className="w-full text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  Try a different method
                </button>
              )}
            </div>
          )}
        </div>
      </Card>
    </AuthLayout>
  )
}
